import { Switch, type SwitchProps } from "@mui/material";
import { forwardRef } from "react";

/**
 * Brand toggle switch — wraps `@mui/material/Switch` with sx overrides that
 * reproduce the prior custom-Tailwind look (h-6 w-11 track, 20x20 thumb,
 * green-400 checked track, grey-200 unchecked, flat-shadow thumb on check).
 *
 * Consumer API is unchanged from the pre-MUI version: pass standard input
 * props (`checked`, `onChange`, `disabled`, …). MUI's onChange still surfaces
 * `e.target.checked`, so existing callers `(e) => e.target.checked` keep
 * working without modification.
 */
type ToggleProps = Omit<SwitchProps, "color" | "size">;

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(function Toggle(
  { sx, ...rest },
  ref,
) {
  return (
    <Switch
      ref={ref}
      disableRipple
      sx={{
        width: 44,
        height: 24,
        padding: 0,
        // Focus visible outline (matches focus-within:outline-2 outline-grey-500 / offset-2).
        "&:has(.Mui-focusVisible)": {
          outline: "2px solid #a7b0ba",
          outlineOffset: "2px",
          borderRadius: "9999px",
        },
        "& .MuiSwitch-switchBase": {
          padding: "2px",
          transitionDuration: "200ms",
          "&.Mui-checked": {
            transform: "translateX(20px)",
            color: "#ffffff",
            "& + .MuiSwitch-track": {
              backgroundColor: "#aad21f", // green-400
              opacity: 1,
              border: 0,
            },
            "& .MuiSwitch-thumb": {
              boxShadow: "0 1px 3px #88AD0C", // matches checked thumb shadow
            },
          },
          "&.Mui-disabled": {
            opacity: 0.6,
            cursor: "not-allowed",
            "& + .MuiSwitch-track": {
              opacity: 0.6,
            },
          },
        },
        "& .MuiSwitch-thumb": {
          width: 20,
          height: 20,
          backgroundColor: "#ffffff",
          boxShadow: "0px 1px 3px #181d2214", // shadow-flat
        },
        "& .MuiSwitch-track": {
          borderRadius: 9999,
          backgroundColor: "#e5ebf1", // grey-200
          opacity: 1,
          transition: "background-color 200ms",
        },
        ...sx,
      }}
      {...rest}
    />
  );
});
